import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { ArrowLeft, TrendingUp, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import BottomNav from '@/components/BottomNav';

const ProfitSharingPage = () => {
  const navigate = useNavigate();
  const [offers, setOffers] = useState({});
  const [loading, setLoading] = useState(true);

  // Fetch offer status
  useEffect(() => {
    const fetchOfferStatus = async () => {
      try {
        const response = await axios.get(`${API}/refer-offers/status`);
        const offerMap = {};
        (response.data.offers || []).forEach(offer => {
          offerMap[offer.id] = offer.is_active;
        });
        setOffers(offerMap);
      } catch (error) {
        console.error('Error fetching offer status:', error);
        // Default to active if API fails
        setOffers({
          friends_10: true,
          friends_30: true,
          restaurants_5: true,
          delivery_partners_5: true,
          friend_100: true,
          partner_150: true
        });
      } finally {
        setLoading(false);
      }
    };
    fetchOfferStatus();
  }, []);

  // Handle offer click
  const handleOfferClick = (offerId, route) => {
    if (offers[offerId] === false) {
      toast.error('This offer is currently unavailable');
      return;
    }
    navigate(route);
  };

  // Check if offer is active
  const isOfferActive = (offerId) => {
    return offers[offerId] !== false;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="p-2 hover:bg-white/20 rounded-full">
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div>
              <h1 className="text-2xl font-bold">1% Profit Sharing</h1>
              <p className="text-amber-100 text-sm">Partner Program</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Intro Card */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-amber-100 rounded-full">
              <TrendingUp className="w-6 h-6 text-amber-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-800">How It Works</h2>
          </div>
          
          <div className="space-y-4">
            {/* Tier 1 - Friends 10 */}
            <div 
              className={`p-4 rounded-xl border transition-all ${
                isOfferActive('friends_10') 
                  ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200' 
                  : 'bg-gray-100 border-gray-200 opacity-60'
              }`}
            >
              <div className="flex items-start gap-3">
                <CheckCircle className={`w-6 h-6 mt-0.5 flex-shrink-0 ${
                  isOfferActive('friends_10') ? 'text-green-600' : 'text-gray-400'
                }`} />
                <div className="flex-1">
                  <p className={isOfferActive('friends_10') ? 'text-gray-800' : 'text-gray-500'}>
                    <span className={`font-bold ${isOfferActive('friends_10') ? 'text-green-700' : 'text-gray-500'}`}>
                      Unlock the first 1%
                    </span> by referring <span className="font-bold">10 friends</span>. After their first successful order, you will receive <span className={`font-bold ${isOfferActive('friends_10') ? 'text-green-600' : 'text-gray-500'}`}>1% profit of FoodMatrix</span> for the whole month.
                  </p>
                  {!isOfferActive('friends_10') && (
                    <p className="text-xs text-gray-400 mt-2 font-medium">Currently unavailable</p>
                  )}
                  <Button
                    onClick={() => handleOfferClick('friends_10', '/partner-program/friends_10')}
                    disabled={!isOfferActive('friends_10')}
                    className={`mt-3 px-6 py-2 rounded-lg font-semibold ${
                      isOfferActive('friends_10')
                        ? 'bg-green-600 hover:bg-green-700 text-white'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                    data-testid="start-friends-10"
                  >
                    Start
                  </Button>
                </div>
              </div>
            </div>

            {/* Tier 2 - Friends 30 */}
            <div 
              className={`p-4 rounded-xl border transition-all ${
                isOfferActive('friends_30') 
                  ? 'bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200' 
                  : 'bg-gray-100 border-gray-200 opacity-60'
              }`}
            >
              <div className="flex items-start gap-3">
                <CheckCircle className={`w-6 h-6 mt-0.5 flex-shrink-0 ${
                  isOfferActive('friends_30') ? 'text-amber-600' : 'text-gray-400'
                }`} />
                <div className="flex-1">
                  <p className={isOfferActive('friends_30') ? 'text-gray-800' : 'text-gray-500'}>
                    <span className={`font-bold ${isOfferActive('friends_30') ? 'text-amber-700' : 'text-gray-500'}`}>
                      Unlock another 1%
                    </span> by referring <span className="font-bold">30 friends</span>. After their first successful order, you will receive <span className={`font-bold ${isOfferActive('friends_30') ? 'text-amber-600' : 'text-gray-500'}`}>1% profit of FoodMatrix</span> for the whole month.
                  </p>
                  {!isOfferActive('friends_30') && (
                    <p className="text-xs text-gray-400 mt-2 font-medium">Currently unavailable</p>
                  )}
                  <Button
                    onClick={() => handleOfferClick('friends_30', '/partner-program/friends_30')}
                    disabled={!isOfferActive('friends_30')}
                    className={`mt-3 px-6 py-2 rounded-lg font-semibold ${
                      isOfferActive('friends_30')
                        ? 'bg-amber-600 hover:bg-amber-700 text-white'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                    data-testid="start-friends-30"
                  >
                    Start
                  </Button>
                </div>
              </div>
            </div>

            {/* Tier 3 - Restaurant Referral */}
            <div 
              className={`p-4 rounded-xl border transition-all ${
                isOfferActive('restaurants_5') 
                  ? 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200' 
                  : 'bg-gray-100 border-gray-200 opacity-60'
              }`}
            >
              <div className="flex items-start gap-3">
                <CheckCircle className={`w-6 h-6 mt-0.5 flex-shrink-0 ${
                  isOfferActive('restaurants_5') ? 'text-blue-600' : 'text-gray-400'
                }`} />
                <div className="flex-1">
                  <p className={isOfferActive('restaurants_5') ? 'text-gray-800' : 'text-gray-500'}>
                    <span className={`font-bold ${isOfferActive('restaurants_5') ? 'text-blue-700' : 'text-gray-500'}`}>
                      Unlock the first 1%
                    </span> by referring <span className="font-bold">5 restaurants</span>. After their successful registration, you will receive <span className={`font-bold ${isOfferActive('restaurants_5') ? 'text-green-600' : 'text-gray-500'}`}>1% profit of FoodMatrix</span> for the whole month.
                  </p>
                  {!isOfferActive('restaurants_5') && (
                    <p className="text-xs text-gray-400 mt-2 font-medium">Currently unavailable</p>
                  )}
                  <Button
                    onClick={() => handleOfferClick('restaurants_5', '/partner-program/restaurants_5')}
                    disabled={!isOfferActive('restaurants_5')}
                    className={`mt-3 px-6 py-2 rounded-lg font-semibold ${
                      isOfferActive('restaurants_5')
                        ? 'bg-blue-600 hover:bg-blue-700 text-white'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                    data-testid="start-restaurants-5"
                  >
                    Start
                  </Button>
                </div>
              </div>
            </div>

            {/* Tier 4 - Delivery Partner Referral */}
            <div 
              className={`p-4 rounded-xl border transition-all ${
                isOfferActive('delivery_partners_5') 
                  ? 'bg-gradient-to-r from-purple-50 to-violet-50 border-purple-200' 
                  : 'bg-gray-100 border-gray-200 opacity-60'
              }`}
            >
              <div className="flex items-start gap-3">
                <CheckCircle className={`w-6 h-6 mt-0.5 flex-shrink-0 ${
                  isOfferActive('delivery_partners_5') ? 'text-purple-600' : 'text-gray-400'
                }`} />
                <div className="flex-1">
                  <p className={isOfferActive('delivery_partners_5') ? 'text-gray-800' : 'text-gray-500'}>
                    <span className={`font-bold ${isOfferActive('delivery_partners_5') ? 'text-purple-700' : 'text-gray-500'}`}>
                      Unlock the first 1%
                    </span> by referring <span className="font-bold">5 delivery partners</span>. After their successful registration, you will receive <span className={`font-bold ${isOfferActive('delivery_partners_5') ? 'text-green-600' : 'text-gray-500'}`}>1% profit of FoodMatrix</span> for the whole month.
                  </p>
                  {!isOfferActive('delivery_partners_5') && (
                    <p className="text-xs text-gray-400 mt-2 font-medium">Currently unavailable</p>
                  )}
                  <Button
                    onClick={() => handleOfferClick('delivery_partners_5', '/partner-program/delivery_partners_5')}
                    disabled={!isOfferActive('delivery_partners_5')}
                    className={`mt-3 px-6 py-2 rounded-lg font-semibold ${
                      isOfferActive('delivery_partners_5')
                        ? 'bg-purple-600 hover:bg-purple-700 text-white'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                    data-testid="start-delivery-partners-5"
                  >
                    Start
                  </Button>
                </div>
              </div>
            </div>

            {/* Tier 5 - Refer Friend Earn ₹100 */}
            <div 
              className={`p-4 rounded-xl border transition-all ${
                isOfferActive('friend_100') 
                  ? 'bg-gradient-to-r from-teal-50 to-cyan-50 border-teal-200' 
                  : 'bg-gray-100 border-gray-200 opacity-60'
              }`}
            >
              <div className="flex items-start gap-3">
                <CheckCircle className={`w-6 h-6 mt-0.5 flex-shrink-0 ${
                  isOfferActive('friend_100') ? 'text-teal-600' : 'text-gray-400'
                }`} />
                <div className="flex-1">
                  <p className={isOfferActive('friend_100') ? 'text-gray-800' : 'text-gray-500'}>
                    <span className={`font-bold ${isOfferActive('friend_100') ? 'text-teal-700' : 'text-gray-500'}`}>
                      Refer a friend
                    </span> and earn <span className={`font-bold ${isOfferActive('friend_100') ? 'text-teal-600' : 'text-gray-500'}`}>₹100</span> after their first order.
                  </p>
                  {!isOfferActive('friend_100') && (
                    <p className="text-xs text-gray-400 mt-2 font-medium">Currently unavailable</p>
                  )}
                  <Button
                    onClick={() => handleOfferClick('friend_100', '/partner-program/friend_100')}
                    disabled={!isOfferActive('friend_100')}
                    className={`mt-3 px-6 py-2 rounded-lg font-semibold ${
                      isOfferActive('friend_100')
                        ? 'bg-teal-600 hover:bg-teal-700 text-white'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                    data-testid="start-friend-100"
                  >
                    Start
                  </Button>
                </div>
              </div>
            </div>

            {/* Tier 6 - Refer Partner Earn ₹150 */}
            <div 
              className={`p-4 rounded-xl border transition-all ${
                isOfferActive('partner_150') 
                  ? 'bg-gradient-to-r from-rose-50 to-pink-50 border-rose-200' 
                  : 'bg-gray-100 border-gray-200 opacity-60'
              }`}
            >
              <div className="flex items-start gap-3">
                <CheckCircle className={`w-6 h-6 mt-0.5 flex-shrink-0 ${
                  isOfferActive('partner_150') ? 'text-rose-600' : 'text-gray-400'
                }`} />
                <div className="flex-1">
                  <p className={isOfferActive('partner_150') ? 'text-gray-800' : 'text-gray-500'}>
                    <span className={`font-bold ${isOfferActive('partner_150') ? 'text-rose-700' : 'text-gray-500'}`}>
                      Refer a restaurant or a delivery partner
                    </span> and earn <span className={`font-bold ${isOfferActive('partner_150') ? 'text-rose-600' : 'text-gray-500'}`}>₹150</span> on their successful registration.
                  </p>
                  {!isOfferActive('partner_150') && (
                    <p className="text-xs text-gray-400 mt-2 font-medium">Currently unavailable</p>
                  )}
                  <Button
                    onClick={() => handleOfferClick('partner_150', '/partner-program/partner_150')}
                    disabled={!isOfferActive('partner_150')}
                    className={`mt-3 px-6 py-2 rounded-lg font-semibold ${
                      isOfferActive('partner_150')
                        ? 'bg-rose-600 hover:bg-rose-700 text-white'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                    data-testid="start-partner-150"
                  >
                    Start
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Info Note */}
        <div className="bg-white rounded-xl shadow-md p-4 text-center">
          <p className="text-sm text-gray-500">Referral tracking and profit sharing features coming soon!</p>
        </div>
      </div>

      <BottomNav />
    </div>
  );
};

export default ProfitSharingPage;
